"use client";

import { useQuery } from "convex/react";
import { motion } from "framer-motion";
import { useState } from "react";

import { api } from "@convex/_generated/api";

import { WordOfWeekArchive } from "./word-of-the-week-archive";
import { WordOfWeekHero } from "./word-of-the-week-hero";
import type { WordOfWeekTranslations, WordOfWeekVideo } from "./word-of-the-week-types";

type WordOfWeekPageProps = {
  locale: string;
  translations: WordOfWeekTranslations;
  initialVideos?: WordOfWeekVideo[];
};

export function WordOfWeekPage({ locale, translations, initialVideos }: WordOfWeekPageProps) {
  // Server-fetched data renders immediately; the live Convex query takes over
  // once the websocket connects. Without the fallback, the whole page
  // (including the LCP heading) waited ~5s for hydration + websocket + query.
  const liveVideos = useQuery(api.wordOfTheWeek.list);
  const videos = liveVideos ?? initialVideos;
  const [selectedId, setSelectedId] = useState<WordOfWeekVideo["_id"] | null>(null);
  const selectedVideo =
    videos?.find((video) => video._id === selectedId) ?? videos?.[0] ?? null;

  /* Loading — skeletal shimmer matching real layout proportions */
  if (videos === undefined) {
    return (
      <div className="grid items-start gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
        <div className="space-y-5 py-4">
          <div className="h-3 w-32 animate-pulse rounded-full bg-zinc-200/60 dark:bg-white/5" />
          <div className="h-20 w-4/5 animate-pulse rounded-2xl bg-zinc-200/60 dark:bg-white/5" />
          <div className="h-4 w-3/5 animate-pulse rounded-full bg-zinc-200/40 dark:bg-white/[0.03]" />
          <div className="mt-auto pt-20">
            <div className="h-12 w-full animate-pulse rounded-xl bg-zinc-200/40 dark:bg-white/[0.03]" />
          </div>
        </div>
        <div className="aspect-[9/16] w-full animate-pulse rounded-[1.75rem] bg-zinc-200/60 dark:bg-white/5" />
      </div>
    );
  }

  /* Empty state — editorial, not a card */
  if (videos.length === 0 || !selectedVideo) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 80, damping: 22 }}
        className="py-24 text-center lg:py-32"
      >
        <h1 className="font-display text-4xl font-black tracking-tighter text-zinc-950 dark:text-white md:text-6xl">
          {translations.emptyTitle}
        </h1>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
          {translations.emptyBody}
        </p>
      </motion.section>
    );
  }

  return (
    <div className="space-y-24 lg:space-y-32">
      <WordOfWeekHero
        locale={locale}
        selectedVideo={selectedVideo}
        translations={translations}
      />
      <WordOfWeekArchive
        selectedId={selectedVideo._id}
        translations={translations}
        videos={videos}
        onSelect={(video) => setSelectedId(video._id)}
      />
    </div>
  );
}
